import React, { useEffect, useRef, useState } from "react";
import { Alert, Checkbox, Form, Input, Modal, Typography } from "antd";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { Success, Error } from "../../../commons/Messages/Messages";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import SignaturePad from "./SignaturePad";

const { Paragraph } = Typography;

/**
 * Collects one party's drawn signature. `inPerson` means the provider's device is handed to
 * the other party (an employee without an account, or an external contract partner).
 */
const SignContractModal = ({ open, onClose, onSigned, contract, party, defaultName, inPerson }) => {
  const { t } = useTranslation("dashboard");
  const [form] = Form.useForm();
  const padRef = useRef(null);
  const [padEmpty, setPadEmpty] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      form.setFieldsValue({ name: defaultName || "", agree: false });
      setPadEmpty(true);
    }
  }, [open]);

  const submit = async () => {
    const values = await form.validateFields();
    if (padRef.current?.isEmpty()) {
      Error(t("Please draw your signature."));
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/contracts/sign/${contract._id}`,
        {
          party,
          name: values.name,
          image: padRef.current.toDataURL(),
          // Proves the signer was shown this exact version of the text.
          documentHash: contract.documentHash
        },
        { headers: { Authorization: "Bearer " + localStorage.getItem("token") } }
      );
      Success(t(res.data.successMessage));
      onSigned?.();
      onClose();
    } catch (err) {
      if (err?.response?.data?.code === "DOCUMENT_CHANGED") {
        Error(t("The contract text has changed. Please reload it before signing."));
        onSigned?.();
        onClose();
      } else {
        generalErrorAlert(err);
      }
    } finally {
      setSaving(false);
    }
  };

  const isFixedTermEmployment = contract?.fixedTerm && contract?.employee;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={submit}
      okText={t("Sign")}
      cancelText={t("Cancel")}
      okButtonProps={{ disabled: padEmpty, loading: saving }}
      title={party === "employer" ? t("Sign for the company") : t("Sign as contract partner")}
      width={640}
      centered
      destroyOnClose
      maskClosable={false}
    >
      {inPerson && (
        <Alert
          type="info"
          showIcon
          className="mb-3"
          message={t("Signing in person")}
          description={t(
            "Hand this device to the contract partner so they can read the contract and sign here themselves."
          )}
        />
      )}
      {isFixedTermEmployment && (
        <Alert
          type="warning"
          showIcon
          className="mb-3"
          message={t("A fixed term must be agreed in writing")}
          description={t(
            "A drawn signature does not meet this requirement. Also sign a printed copy by hand."
          )}
        />
      )}
      <Form form={form} layout="vertical">
        <Form.Item
          label={t("Full name of the signer")}
          name="name"
          rules={[{ required: true, whitespace: true, message: t("enter", { field: t("Name") }) }]}
        >
          <Input maxLength={200} />
        </Form.Item>
        <Form.Item label={t("Signature")} required>
          <SignaturePad ref={padRef} onChange={setPadEmpty} />
        </Form.Item>
        <Form.Item
          name="agree"
          valuePropName="checked"
          rules={[
            {
              validator: (_, value) =>
                value
                  ? Promise.resolve()
                  : Promise.reject(new window.Error(t("Please confirm that you agree.")))
            }
          ]}
        >
          <Checkbox>
            {t(
              "I have read the contract and agree to its terms. This drawn signature is my electronic signature."
            )}
          </Checkbox>
        </Form.Item>
      </Form>
      <Paragraph type="secondary" className="mb-0 contract-legal-note">
        {t(
          "The time of signing, your name and a checksum of the contract text are stored with the signature."
        )}
      </Paragraph>
    </Modal>
  );
};

export default SignContractModal;
